import { TColumnType } from 'components/Base/Table/Table.types';
import { PlusOutlined } from '@ant-design/icons';
import React from 'react';
import Button from 'components/Base/Button/Button';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  TGetActionPlansResponse,
  pillarsActionPlanApi,
} from 'redux/apiSlice/pillarsActionPlan.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { Paragraph } from '@packages/utils';
import { Icon, EIconNames, SharedIcon } from 'components/Base/Icon';
import { StatusType, TTacticTask } from './TacticTable.types';
import TacticStatusDropdown from './TacticStatusDropdown/TacticStatusDropdown';
import {
  DeleteTrashOutline,
  EditPencilOutline,
} from '../../../../../assets/icon/icon';

export const useGetColumnsData = (
  handleAddTask: (record: TTacticTask) => void,
  handleEditTactic: (record: TTacticTask) => void,
  handleDeleteTactic: (record: TTacticTask) => void,
  actionPlansData?: TGetActionPlansResponse,
  refetch?: () => void,
  isAllElcs?: boolean,
) => {
  const [updateActionPlan] = pillarsActionPlanApi.useUpdateActionPlanMutation();
  const { showToastNotification } = useShowToastNotification();
  const updatingIdsRef = React.useRef<Set<number>>(new Set());
  const optimisticDatesRef = React.useRef<Map<number, string>>(new Map());
  const isMountedRef = React.useRef(true);
  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );
  const pillarFilters = [
    { text: 'Communities', value: 'Communities' },
    { text: 'Services', value: 'Services' },
    { text: 'Neighborhoods', value: 'Neighborhoods' },
    { text: 'Policies', value: 'Policies' },
  ];

  // Use tactic title filters from the API response
  const tacticTitleFilters = React.useMemo(() => {
    if (
      actionPlansData?.filters?.tacticTitles &&
      actionPlansData.filters.tacticTitles.length > 0
    ) {
      return actionPlansData.filters.tacticTitles.map(title => ({
        text: title,
        value: title,
      }));
    }

    return [];
  }, [actionPlansData]);

  const hasOnlyCompletedTasks = (record: TTacticTask): boolean => {
    const { completedCount, todoCount, inProgressCount, stalledCount } = record;
    return (
      completedCount > 0 && !todoCount && !inProgressCount && !stalledCount
    );
  };

  const isDeleteDisabled = (record: TTacticTask): boolean => {
    return (
      record.isPreviousPlan ||
      hasOnlyCompletedTasks(record) ||
      (!isAllElcs && record.isGlobal)
    );
  };

  const getDeleteTooltip = (record: TTacticTask): string => {
    if (record.isPreviousPlan) {
      return 'Tactics from previous action plans cannot be deleted';
    }
    if (hasOnlyCompletedTasks(record)) {
      return 'Completed tactics cannot be deleted';
    }
    if (!isAllElcs && record.isGlobal) {
      return 'Global tactics cannot be deleted from individual ELC action plans';
    }
    return 'Remove';
  };

  const isEditDisabled = (record: TTacticTask): boolean => {
    return record.isPreviousPlan || (!isAllElcs && record.isGlobal);
  };

  const getEditTooltip = (record: TTacticTask): string => {
    if (record.isPreviousPlan) {
      return 'Tactics from previous action plans cannot be edited';
    }
    if (!isAllElcs && record.isGlobal) {
      return 'Global tactics cannot be edited from individual ELC action plans';
    }
    return 'Edit Tactic';
  };

  const getAddTaskTooltip = (record: TTacticTask): string => {
    if (record.isPreviousPlan) {
      return 'This tactic is from a previous plan and cannot be modified';
    }
    if (isAllElcs) {
      return 'Adding tasks is disabled on the global page';
    }
    return 'Add Task';
  };

  const defaultColumns: TColumnType<TTacticTask>[] = [
    {
      title: 'Pillar',
      dataIndex: 'pillarName',
      key: 'pillarName',
      width: 140,
      filters: pillarFilters,
      onFilter: () => true,
      render: (value?: string) => (
        <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
      ),
    },
    {
      title: 'Goal',
      dataIndex: 'goalDescription',
      key: 'goalDescription',
      width: 260,
      ellipsis: true,
      render: (value?: string) => {
        if (!value) return <span>-</span>;
        return (
          <Paragraph
            ellipsis={{ tooltip: value }}
            style={{ marginBottom: 0, minWidth: 0 }}
          >
            {value}
          </Paragraph>
        );
      },
    },
    {
      title: 'Level',
      dataIndex: 'levelName',
      key: 'levelName',
      width: 90,
      render: (value?: string) => (
        <span style={{ whiteSpace: 'nowrap' }}>{value || ' '}</span>
      ),
    },
    {
      title: 'Tactic Title',
      dataIndex: 'tacticTitle',
      key: 'tacticTitle',
      width: 320,
      ellipsis: true,
      filters: tacticTitleFilters,
      onFilter: () => true,
      render: (text: string, record: TTacticTask) => {
        const iconCount =
          (record.isCustomTactic ? 1 : 0) + (record.isGlobal ? 1 : 0);
        const paddingRight = iconCount * 24;
        const customIconRight = record.isGlobal ? 26 : 0;
        return (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Paragraph
              ellipsis={{ tooltip: text }}
              style={{
                marginBottom: 0,
                flex: 1,
                minWidth: 0,
                paddingRight,
              }}
            >
              {text}
            </Paragraph>
            {record.isCustomTactic && (
              <span
                style={{
                  position: 'absolute',
                  right: customIconRight,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'inline-flex',
                }}
                data-testid="custom-tactic-icon"
              >
                <Icon
                  icon={EIconNames.FILE}
                  size={18}
                  alt="Custom Tactic"
                  tooltip={{ title: 'Custom Tactic' }}
                />
              </span>
            )}
            {record.isGlobal && (
              <SharedIcon
                size={18}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                data-testid="global-tactic-icon"
                alt="Global Shared Tactic"
                title="Global Shared Tactic"
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Tactic Status',
      key: 'tacticStatus',
      width: 240,
      filters: [
        { text: 'To Do', value: 'TODO' },
        { text: 'In Progress', value: 'IN_PROGRESS' },
        { text: 'Stalled', value: 'STALLED' },
        { text: 'Completed', value: 'COMPLETED' },
      ],
      onFilter: () => true,
      render: (_, record) => {
        let derivedStatus: StatusType = 'TODO';
        if (record.inProgressCount > 0) derivedStatus = 'IN_PROGRESS';
        else if (record.stalledCount > 0) derivedStatus = 'STALLED';
        else if (record.completedCount > 0) derivedStatus = 'COMPLETED';

        return (
          <TacticStatusDropdown
            value={(record.status as StatusType) || derivedStatus}
            counts={{
              todo: record.todoCount,
              inProgress: record.inProgressCount,
              stalled: record.stalledCount,
              completed: record.completedCount,
            }}
            onChange={async status => {
              if (status === 'COMPLETED') return;
              if (updatingIdsRef.current.has(record.actionPlanId)) return;
              updatingIdsRef.current.add(record.actionPlanId);
              try {
                if (optimisticDatesRef.current.has(record.actionPlanId)) {
                  optimisticDatesRef.current.delete(record.actionPlanId);
                }

                await updateActionPlan({
                  actionPlanId: record.actionPlanId,
                  field: 'STATUS',
                  value: status,
                }).unwrap();
                if (isMountedRef.current) {
                  showToastNotification({
                    type: ENotificationType.SUCCESS,
                    message: 'Tactic status updated.',
                  });
                }
              } catch (e) {
                if (isMountedRef.current) {
                  showToastNotification({
                    type: ENotificationType.ERROR,
                    message:
                      'Failed to update tactic status. Please try again.',
                  });
                }
              } finally {
                updatingIdsRef.current.delete(record.actionPlanId);
              }
            }}
            onComplete={async date => {
              if (updatingIdsRef.current.has(record.actionPlanId)) return;
              updatingIdsRef.current.add(record.actionPlanId);
              try {
                const formatted = dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
                optimisticDatesRef.current.set(record.actionPlanId, formatted);

                await updateActionPlan({
                  actionPlanId: record.actionPlanId,
                  field: 'STATUS',
                  value: 'COMPLETED',
                }).unwrap();

                await updateActionPlan({
                  actionPlanId: record.actionPlanId,
                  field: 'DATE_COMPLETED',
                  value: formatted,
                }).unwrap();

                if (isMountedRef.current && refetch) {
                  await refetch();
                }

                if (isMountedRef.current) {
                  showToastNotification({
                    type: ENotificationType.SUCCESS,
                    message: 'Tactic marked as completed.',
                  });
                }
              } catch (e) {
                optimisticDatesRef.current.delete(record.actionPlanId);
                if (isMountedRef.current) {
                  showToastNotification({
                    type: ENotificationType.ERROR,
                    message:
                      'Failed to mark tactic as completed. Please try again.',
                  });
                }
              } finally {
                updatingIdsRef.current.delete(record.actionPlanId);
              }
            }}
          />
        );
      },
    },
    {
      title: 'Completed',
      key: 'completed',
      width: 150,
      sorter: true,
      render: (_, record) => {
        const dateToDisplay =
          record.dateCompleted ||
          optimisticDatesRef.current.get(record.actionPlanId);

        if (!dateToDisplay) return '-';

        const d = dayjs(dateToDisplay);

        if (
          record.dateCompleted &&
          optimisticDatesRef.current.has(record.actionPlanId)
        ) {
          optimisticDatesRef.current.delete(record.actionPlanId);
        }

        return d.isValid() ? d.format('MMM DD, YYYY') : '-';
      },
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Repeat</span>,
      dataIndex: 'repeatOrder',
      key: 'repeatOrder',
      width: 100,
      align: 'end',
      sorter: (a, b) => a.repeatOrder - b.repeatOrder,
    },
    {
      title: 'Actions',
      key: 'tactic-actions',
      width: 140,
      align: 'end',
      render: (_, record) => (
        <Flex gap={4} justify="flex-end">
          {record.isCustomTactic && (
            <Button
              variant="ghost"
              onClick={() => handleEditTactic(record)}
              aria-label="Edit tactic"
              data-cy="edit-tactic-btn"
              disabled={isEditDisabled(record)}
              tooltip={getEditTooltip(record)}
            >
              <EditPencilOutline
                size={16}
                fill={isEditDisabled(record) ? '#B5B5B5' : undefined}
              />
            </Button>
          )}

          <Button
            variant="ghost"
            onClick={() => handleAddTask(record)}
            aria-label="Add task"
            data-cy="add-task-btn"
            disabled={record.isPreviousPlan || isAllElcs}
            tooltip={getAddTaskTooltip(record)}
          >
            <PlusOutlined
              size={16}
              style={{
                color:
                  record.isPreviousPlan || isAllElcs ? '#B5B5B5' : undefined,
              }}
            />
          </Button>

          <Button
            variant="ghost"
            onClick={() => handleDeleteTactic(record)}
            aria-label="Delete tactic"
            data-cy="delete-tactic-btn"
            disabled={isDeleteDisabled(record)}
            tooltip={getDeleteTooltip(record)}
          >
            <DeleteTrashOutline
              size={16}
              fill={isDeleteDisabled(record) ? '#B5B5B5' : undefined}
            />
          </Button>
        </Flex>
      ),
    },
  ];
  return { defaultColumns };
};

export default useGetColumnsData;
